import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Message, Segment, Table, Icon, Button } from 'semantic-ui-react';
import i18next from 'i18next';
import { attachChainId } from '../nav';
import CurrencyFormat from '../general/CurrencyFormat';
import Loading from '../loading/Loading';
import { usePageTitle } from '../../hooks';
import { getAssets } from '../../selectors';
import api from '../../api/paidit-api';

import './OrderConfirmation.scss';

const OrderConfirmation = (props) => {
    const orderGuid = new URLSearchParams(props.location.search).get('orderGuid');
    const isLoading = useSelector(state => state.loading.queue.length > 0);
    const { mainBackground } = useSelector(getAssets);
    const [confirmation, setConfirmaiton] = useState(null);

    usePageTitle('pageTitles.orderConfirmed');

    useEffect(() => {
        if (orderGuid) {
            api({ alertError: null }).order.confirmation(orderGuid)
                .then(result => setConfirmaiton(result))
                .catch(() => props.history.replace(`/notFound`));
        }
    }, [orderGuid, props.history]);

    if (!confirmation) return <Loading showLoading={isLoading} />;

    const { cart, payments, restaurant, delivery } = confirmation;
    const tip = payments.reduce((total, payment) => total + (payment.tip || 0), 0);
    const tax = payments.reduce((total, payment) => total + (payment.tax || 0), 0);
    const surcharge = payments.reduce((total, payment) => total + (payment.surcharge || 0), 0);

    return (
        <div style={{ height: '100%', backgroundImage: `url(${mainBackground}` }}>
            <div className="pi-order-confirmation" >
                <Message
                    icon='check'
                    success
                    header={i18next.t('confirmation.success')}
                    content={payments.some(p => !!p.customer.email) ? i18next.t('confirmation.emailSent') : ''}
                />
                <Segment>
                    <Table basic='very' columns={2} padded>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell>{i18next.t('confirmation.orderId')}</Table.Cell>
                                <Table.Cell>{'#' + confirmation.id}</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>{i18next.t('confirmation.orderType')}</Table.Cell>
                                <Table.Cell>{i18next.t('confirmation.orderTypes.' + cart.orderType)}</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>{i18next.t('confirmation.subtotal')} ({cart.items.length})</Table.Cell>
                                <Table.Cell>
                                    <CurrencyFormat amount={cart.subtotal} currency={restaurant.currencyCode} />
                                </Table.Cell>
                            </Table.Row>
                            {!!delivery &&
                                <Table.Row>
                                    <Table.Cell>{i18next.t('confirmation.deliveryFees')}</Table.Cell>
                                    <Table.Cell>
                                        <CurrencyFormat amount={delivery.fee} currency={restaurant.currencyCode} />
                                    </Table.Cell>
                                </Table.Row>
                            }
                            {!!tip &&
                                <Table.Row>
                                    <Table.Cell>{i18next.t('confirmation.tip')}</Table.Cell>
                                    <Table.Cell>
                                        <CurrencyFormat amount={tip} currency={restaurant.currencyCode} />
                                    </Table.Cell>
                                </Table.Row>
                            }
                            {!!tax &&
                                <Table.Row>
                                    <Table.Cell>{i18next.t('confirmation.tax')}</Table.Cell>
                                    <Table.Cell>
                                        <CurrencyFormat amount={tax} currency={restaurant.currencyCode} />
                                    </Table.Cell>
                                </Table.Row>
                            }
                            {!!surcharge &&
                                <Table.Row>
                                    <Table.Cell>{i18next.t('confirmation.surcharge')}</Table.Cell>
                                    <Table.Cell>
                                        <CurrencyFormat amount={surcharge} currency={restaurant.currencyCode} />
                                    </Table.Cell>
                                </Table.Row>
                            }
                            <Table.Row>
                                <Table.Cell>{i18next.t('confirmation.total')}</Table.Cell>
                                <Table.Cell>
                                    <CurrencyFormat
                                        amount={payments.reduce((total, payment) => total + payment.amount, 0)}
                                        currency={restaurant.currencyCode}
                                    />
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </Segment>
                <Segment.Group>
                    <Segment style={{ display: 'flex', alignItems: 'center' }}>
                        <Icon name="info" style={{ margin: 5 }} />
                        <div >
                            {i18next.t('confirmation.business')}
                            <br />
                            {[restaurant.name, restaurant.address, restaurant.phone].filter(value => !!value).join(' | ')}
                        </div>
                    </Segment>
                    {!delivery ? null :
                        <Segment style={{ display: 'flex', alignItems: 'center' }}>
                            <Icon name="map marker" style={{ margin: 5 }} />
                            <div >
                                {i18next.t('confirmation.deliveryAddress')}
                                <br />
                                {[delivery.fullAddress].concat(['floor', 'entrance', 'apartment', 'remarks'].filter(key => !!delivery[key]).map(key => `${i18next.t('address.' + key)}: ${delivery[key]}`)).join(' | ')}
                            </div>
                        </Segment>
                    }
                    <Segment style={{ display: 'flex', alignItems: 'center' }}>
                        <Icon name="user" style={{ margin: 5 }} />
                        <div >
                            {i18next.t('confirmation.contact')}
                            <br />
                            {payments.map(({ customer }) => [customer.name, customer.phone, customer.email, customer.tableNumber && i18next.t('confirmation.tableNumber', { number: customer.tableNumber })].filter(value => !!value).join(' | ')).join(' | ')}
                        </div>
                    </Segment>
                </Segment.Group>
                <Button size='big' fluid secondary onClick={() => { window.location.replace(attachChainId('/', restaurant.ownerId)); }}>
                    {i18next.t('confirmation.startNew')}
                </Button>
            </div>
        </div>
    );
};

OrderConfirmation.propTypes = {
    location: PropTypes.object,
    history: PropTypes.object
};

export default OrderConfirmation;
